<script setup>
import {Dialog, DialogOverlay, DialogPanel, TransitionChild, TransitionRoot} from '@headlessui/vue';
import {computed, ref} from 'vue';
import {Icon} from "@iconify/vue";
import {useChatStore} from "@/stores/chat.js";
import showAlert from "@/utils/showAlert.js";
import {showMessage} from "@/utils/showMessage.js";
import {useConfigStore} from "@/stores/config.js";

const emit = defineEmits(['closeModal']);
const isLoading = ref(false);
const chatStore = useChatStore();
const configStore = useConfigStore();
const isKnownGame = (game) => configStore.getAllGames.some(item => item.game === game);
const form = ref({
  "channel": chatStore.selectedChat.channel,
  "orderType": chatStore.chatData.orderType,
  "orderGame": isKnownGame(chatStore.chatData.orderGame) ? chatStore.chatData.orderGame : ''
})
const isFormValid = computed(() => isKnownGame(form.value.orderGame));

function closeModal() {
  emit('closeModal');
}

async function customizeChannel() {
  //const ok = await showAlert('You are setting data for chat', 'Are you sure?', 'Yes', 'No');
  //if (!ok) return

  if (!isFormValid.value) {
    form.value.orderGame = '';
    showMessage('Please select a valid game', 'error');
    return;
  }

  isLoading.value = true
  const result = await chatStore.action_customizeChannel(form.value);
  if (result === true) {
    showMessage('Data changed', 'success');
    isLoading.value = false
    closeModal()
    await chatStore.action_get_chat();
  } else {
    showMessage(result, 'error');
    isLoading.value = false;
  }
}
</script>

<template>
  <TransitionRoot appear show as="template">
    <Dialog as="div" @close="closeModal" class="relative z-[999]">
      <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-[black]/60"/>
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex h-full overflow-hidden items-center justify-center px-4 py-8">
          <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
          >
            <DialogPanel
                class="w-full flex flex-col overflow-hidden rounded-md max-w-max h-auto text-left align-middle shadow-xl transition-all bg-white dark:bg-[#0e1726] text-black-600 dark:text-white">
              <div class="h-full flex flex-col">
                <div class="flex flex-row gap-10 justify-between px-4 border-b border-slate-700">
                  <div class="text-base py-3">
                    Set chat data
                  </div>
                  <div class="flex flex-row items-center gap-4">
                    <div class="h-full w-[1px] bg-slate-700"></div>
                    <button
                        type="button"
                        class="text-white-dark outline-none hover:text-red-500"
                        @click="closeModal"
                    >
                      <Icon icon="pajamas:close-xs"/>
                    </button>
                  </div>
                </div>
                <div class="p-4 flex-1 overflow-auto h-full flex flex-col gap-6">
                  <p v-if="isLoading">Loading...</p>
                  <div v-else class="space-y-5">
                    <div class="flex flex-col gap-1 text-sm">
                      <label for="orderType">Service</label>
                      <div class="">
                        <select id="orderType" v-model="form.orderType"
                                class="bg-slate-900 h-[40px] rounded-md border border-slate-700 px-1 w-full">
                          <option v-for="service in chatStore.allServices">{{ service.label }}</option>
                        </select>
                      </div>
                    </div>
                    <div class="flex flex-col gap-1 text-sm">
                      <label for="orderGame">Game</label>
                      <div class="">
                        <select id="orderGame" v-model="form.orderGame"
                                class="bg-slate-900 h-[40px] rounded-md border border-slate-700 px-1">
                          <option disabled value="">Select a game</option>
                          <option v-for="game in configStore.getAllGames" :key="game.game" :value="game.game">
                            {{ game.game }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="p-3 flex justify-end gap-3">
                  <button @click="customizeChannel" type="submit" class="btn btn-outline-primary py-1"
                          :disabled="isLoading || !isFormValid">
                    <span v-if="isLoading"
                          class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0"></span>
                    Save
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>

</style>
